import {
    getSowCreditPoolLabel,
    resolveSowPoolUsageDisplayMetrics,
    type SowPoolUsageDisplayMetrics,
    type SowPoolUsageScope,
} from '@wevo/wevo-access/sow-credit-pools';
import {
    LedgerEntrySummary,
    Organization,
    SowBalanceSummary,
    SowSummary,
} from '../../../services/api';

export interface SowCreditRow {
    id: string;
    companyId: number;
    companyName: string;
    name: string;
    state: string;
    startDate: string;
    endDate?: string | null;
    granted: number;
    available: number;
    used: number;
    usagePercent: number;
    committedUsagePercent: number;
    pendingUsagePercent: number;
}

export interface CompanySowCreditRow {
    id: string;
    name: string;
    startDate: string;
    endDate?: string | null;
    state: string;
    available: number;
    proUsage: SowPoolUsageDisplayMetrics | null;
    pulseUsage: SowPoolUsageDisplayMetrics | null;
}

export interface PoolBalanceRow {
    poolType: string;
    label: string;
    contracted: number;
    released: number;
    fromRollover: number;
    granted: number;
    /** Denominator for usage progress (current period allocation for periodic release pools). */
    usageGranted: number;
    usageScope: SowPoolUsageScope;
    /** Lifetime spendable balance from the API. */
    available: number;
    /** Available credits within the same scope as usageGranted (current period or lifetime). */
    usageAvailable: number;
    used: number;
    expiredByPolicy: number;
    usagePercent: number;
    committedUsagePercent: number;
    pendingUsagePercent: number;
    /** Usage amounts backing the progress bar (current period for periodic release pools). */
    usageCommitted: number;
    usagePending: number;
    resetPolicy?: Record<string, unknown>;
    currentPeriodStart?: string | null;
    currentPeriodEnd?: string | null;
    periodSummaries: NonNullable<SowBalanceSummary['pools'][number]['periodSummaries']>;
}

function toNumber(value: string | number | undefined): number {
    if (value === undefined) return 0;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
}

function getCompanyId(organization: Organization): number | null {
    const rawCompanyId = organization.metadata?.company_id;
    const parsed = typeof rawCompanyId === 'string' ? Number(rawCompanyId) : undefined;
    return parsed && Number.isFinite(parsed) ? parsed : null;
}

function getCompanyName(organization: Organization | undefined, companyId: number): string {
    return organization?.display_name || organization?.name || `Company ${companyId}`;
}

function usagePercent(granted: number, used: number): number {
    if (granted <= 0) return 0;
    return Math.round((used / granted) * 100);
}

/** Maps positive usage totals to a 0-100% segment of granted credits. */
function usageSegmentPercentOfGranted(granted: number, usageTotal: number): number {
    if (granted <= 0) return 0;
    const spend = usageTotal > 0 ? usageTotal : 0;
    return Math.min(100, Math.round((spend / granted) * 100));
}

export function toSowCreditRows(sows: SowSummary[], organizations: Organization[]): SowCreditRow[] {
    const organizationsByCompanyId = new Map<number, Organization>();
    organizations.forEach((organization) => {
        const companyId = getCompanyId(organization);
        if (companyId) {
            organizationsByCompanyId.set(companyId, organization);
        }
    });

    return sows.map((sow) => {
        const balance = sow.balanceSummary ?? undefined;
        const granted = toNumber(balance?.totals.granted ?? balance?.totals.released);
        const available = toNumber(balance?.totals.available);
        const committedUsageTotal = toNumber(balance?.totals.usageCommitted);
        const pendingUsageTotal = toNumber(balance?.totals.usagePending);
        const used = Math.max(committedUsageTotal + pendingUsageTotal, 0);

        return {
            id: sow.id,
            companyId: sow.companyId,
            companyName: getCompanyName(organizationsByCompanyId.get(sow.companyId), sow.companyId),
            name: sow.name,
            state: sow.state,
            startDate: sow.startDate,
            endDate: sow.endDate,
            granted,
            available,
            used,
            usagePercent: usagePercent(granted, used),
            committedUsagePercent: usageSegmentPercentOfGranted(granted, committedUsageTotal),
            pendingUsagePercent: usageSegmentPercentOfGranted(granted, pendingUsageTotal),
        };
    });
}

function toPoolUsageDisplayMetrics(
    pool: SowBalanceSummary['pools'][number] | undefined,
): SowPoolUsageDisplayMetrics | null {
    if (!pool) {
        return null;
    }
    return resolveSowPoolUsageDisplayMetrics({
        granted: pool.granted,
        available: pool.available,
        usagePending: pool.usagePending,
        usageCommitted: pool.usageCommitted,
        resetPolicy: pool.resetPolicy,
        periodSummaries: pool.periodSummaries,
    });
}

export function toCompanySowCreditRows(sows: SowSummary[]): CompanySowCreditRow[] {
    return sows.map((sow) => {
        const balance = sow.balanceSummary ?? undefined;
        const pools = balance?.pools ?? [];
        const poolsByType = new Map(pools.map((pool) => [pool.poolType, pool]));

        return {
            id: sow.id,
            name: sow.name,
            state: sow.state,
            startDate: sow.startDate,
            endDate: sow.endDate,
            available: toNumber(balance?.totals.available),
            proUsage: toPoolUsageDisplayMetrics(poolsByType.get('pro_credit')),
            pulseUsage: toPoolUsageDisplayMetrics(poolsByType.get('pulse_credit')),
        };
    });
}

export function formatCredits(value: number): string {
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(value);
}

export function getPoolLabel(poolType: string): string {
    return getSowCreditPoolLabel(poolType) ?? poolType.replace(/_/g, ' ');
}

export function isPeriodicReleasePool(resetPolicy?: Record<string, unknown>): boolean {
    return resetPolicy?.type === 'periodic_release';
}

export interface PoolGrantedBreakdown {
    /** Credits from the SOW contract allocation currently included in granted. */
    contracted: number;
    rolledIn: number;
    additionalGrants: number;
    totalGranted: number;
}

/** Decomposes granted credits into contract, rollover, and manual grant portions. */
export function getPoolGrantedBreakdown(
    pool: Pick<PoolBalanceRow, 'contracted' | 'released' | 'fromRollover' | 'granted'>,
): PoolGrantedBreakdown {
    const contracted = Math.max(pool.released, 0);
    const rolledIn = Math.max(pool.fromRollover, 0);
    const additionalGrants = Math.max(pool.granted - contracted - rolledIn, 0);

    return {
        contracted,
        rolledIn,
        additionalGrants,
        totalGranted: pool.granted,
    };
}

const LEDGER_ENTRY_TYPE_LABELS: Record<string, string> = {
    grant: 'Grant',
    usage: 'Usage',
    usage_hold: 'Reserved',
    hold_adjustment: 'Hold adjustment',
    hold_release: 'Hold release',
    usage_commit: 'Usage commit',
    adjustment: 'Adjustment',
    expiration: 'Expiration',
    rollover: 'Rollover',
    rollover_in: 'Rollover in',
    rollover_out: 'Rollover out',
    import: 'Import',
    refund: 'Refund',
    reassignment: 'Reassignment',
    reassignment_in: 'Reassignment in',
    reassignment_out: 'Reassignment out',
};

export function getLedgerEntryTypeLabel(entryType: string): string {
    return LEDGER_ENTRY_TYPE_LABELS[entryType] ?? entryType.replace(/_/g, ' ');
}

/** Condensed type label for the ledger table: committed usage collapses to "Spent". */
const SIMPLIFIED_LEDGER_TYPE_LABELS: Record<string, string> = {
    usage: 'Spent',
    usage_commit: 'Spent',
};

function isLinkedLedgerEntry(entry: LedgerEntrySummary): boolean {
    return typeof entry.linkedLedgerEntryId === 'string' && entry.linkedLedgerEntryId.trim() !== '';
}

/** A hold that edits another ledger item (e.g. an adjustment to a pending reservation). */
export function isHoldAdjustmentEntry(entry: LedgerEntrySummary): boolean {
    const entryType = getLedgerItemType(entry);
    return (
        entryType === 'hold_adjustment' ||
        (entryType === 'usage_hold' && isLinkedLedgerEntry(entry))
    );
}

export function getLedgerTypeDisplayLabel(entry: LedgerEntrySummary): string {
    const entryType = getLedgerItemType(entry);
    // A hold that edits another ledger item is an adjustment, whether explicit or legacy-linked.
    if (entryType === 'hold_adjustment') {
        return 'Hold adjustment';
    }
    if (entryType === 'usage_hold') {
        return isHoldAdjustmentEntry(entry) ? 'Hold adjustment' : 'Reserved';
    }
    return SIMPLIFIED_LEDGER_TYPE_LABELS[entryType] ?? getLedgerEntryTypeLabel(entryType);
}

const CREDIT_TRANSACTION_STATUS_LABELS: Record<string, string> = {
    pending: 'Reserved',
    committed: 'Spent',
    cancelled: 'Cancelled',
};

export function getCreditReservationStatusLabel(status: string): string {
    return CREDIT_TRANSACTION_STATUS_LABELS[status] ?? status.replace(/_/g, ' ');
}

export function getLedgerReason(entry: LedgerEntrySummary): string | null {
    const reason = entry.auditReason;
    return typeof reason === 'string' && reason.trim() !== '' ? reason.trim() : null;
}

/** Display label for a linked artifact resource id prefix. */
const LINKED_ARTIFACT_TYPE_LABELS: Record<string, string> = {
    pro_study: 'Pro',
    pro_project: 'Pro',
    professional_services: 'Professional services',
    userlytics: 'Userlytics',
    pulse: 'Pulse',
    pulse_set_path: 'Set Path',
    pulse_compare: 'Compare',
    pulse_goal_navigation: 'Goal Navigation',
    pulse_findings_task: 'Pulse',
    automated_insight_session_task: 'Pulse',
};

export function formatLinkedArtifactsTypeLabel(raw: string | null | undefined): string | null {
    if (raw == null || typeof raw !== 'string') return null;
    const t = raw.trim();
    if (t === '') return null;
    return LINKED_ARTIFACT_TYPE_LABELS[t] ?? t.replace(/_/g, ' ');
}

/** Manual entry options should match the direct linked-artifact columns on reservations/ledger rows. */
export const MANUAL_LEDGER_LINKED_ARTIFACT_TYPE_OPTIONS: ReadonlyArray<{
    value: string;
    label: string;
}> = [
    { value: 'pro_study', label: 'Pro study' },
    { value: 'professional_services', label: 'Professional services' },
    { value: 'pulse', label: 'Pulse' },
    { value: 'pulse_set_path', label: 'Set Path' },
    { value: 'pulse_compare', label: 'Compare' },
    { value: 'pulse_goal_navigation', label: 'Goal Navigation' },
    { value: 'userlytics', label: 'Userlytics' },
];

const MANUAL_LEDGER_ARTIFACT_DEFAULT_POOL: Record<string, string> = {
    pro_study: 'pro_credit',
    pulse: 'pulse_credit',
    pulse_set_path: 'pulse_credit',
    pulse_compare: 'pulse_credit',
    pulse_goal_navigation: 'pulse_credit',
    pulse_findings_task: 'pulse_credit',
    automated_insight_session_task: 'pulse_credit',
    professional_services: 'professional_services_credit',
    userlytics: 'userlytics_credit',
};

const MANUAL_LEDGER_PRO_CREDIT_COMPATIBLE_ARTIFACTS = new Set([
    'professional_services',
    'userlytics',
]);

/** Pool types that may fund a manual usage row for the given linked artifact type. */
export function getManualLedgerCompatiblePoolTypes(linkedArtifactType: string): string[] {
    const artifact = linkedArtifactType.trim();
    const defaultPool = MANUAL_LEDGER_ARTIFACT_DEFAULT_POOL[artifact];
    if (!defaultPool) {
        return ['token'];
    }

    const pools = [defaultPool];
    if (MANUAL_LEDGER_PRO_CREDIT_COMPATIBLE_ARTIFACTS.has(artifact)) {
        pools.push('pro_credit');
    }
    pools.push('token');
    return pools;
}

export function isManualLedgerPoolCompatibleWithArtifact(
    poolType: string,
    linkedArtifactType: string,
): boolean {
    if (!poolType.trim() || !linkedArtifactType.trim()) {
        return true;
    }
    return getManualLedgerCompatiblePoolTypes(linkedArtifactType).includes(poolType);
}

export function getManualLedgerPoolArtifactMismatchMessage(
    poolType: string,
    linkedArtifactType: string,
): string | undefined {
    if (isManualLedgerPoolCompatibleWithArtifact(poolType, linkedArtifactType)) {
        return undefined;
    }

    const artifactLabel =
        formatLinkedArtifactsTypeLabel(linkedArtifactType) ?? linkedArtifactType.trim();
    const allowedPoolLabels = getManualLedgerCompatiblePoolTypes(linkedArtifactType)
        .filter((pool) => pool !== 'token')
        .map(getPoolLabel);
    const allowedText =
        allowedPoolLabels.length > 0 ? `${allowedPoolLabels.join(', ')}, or Tokens` : 'Tokens';

    return `${artifactLabel} usage must be charged to ${allowedText}.`;
}

const BADGE_RING = 'ring-1 ring-inset';

/** Base pill shape for admin credit table badges (soft corners, not fully round). */
export const adminCreditBadgeClassName =
    'inline-flex max-w-max rounded-md px-2 py-0.5 text-xs font-medium';

/** Pill styles for ledger entry type badges (admin table). */
export function ledgerEntryTypeBadgeClass(entryType: string): string {
    switch (entryType) {
        case 'grant':
            return `bg-emerald-50 text-emerald-900 ${BADGE_RING} ring-emerald-600/25`;
        case 'usage':
        case 'usage_hold':
        case 'hold_adjustment':
        case 'usage_commit':
            return `bg-sky-50 text-sky-900 ${BADGE_RING} ring-sky-600/25`;
        case 'hold_release':
            return `bg-blue-50 text-blue-900 ${BADGE_RING} ring-blue-600/25`;
        case 'adjustment':
            return `bg-amber-50 text-amber-950 ${BADGE_RING} ring-amber-600/25`;
        case 'expiration':
            return `bg-orange-50 text-orange-950 ${BADGE_RING} ring-orange-600/25`;
        case 'rollover':
        case 'rollover_in':
            return `bg-teal-50 text-teal-900 ${BADGE_RING} ring-teal-600/25`;
        case 'rollover_out':
            return `bg-indigo-50 text-indigo-900 ${BADGE_RING} ring-indigo-600/25`;
        case 'import':
            return `bg-slate-100 text-slate-800 ${BADGE_RING} ring-slate-400/35`;
        case 'refund':
            return `bg-rose-50 text-rose-900 ${BADGE_RING} ring-rose-600/25`;
        case 'reassignment':
        case 'reassignment_in':
        case 'reassignment_out':
            return `bg-purple-50 text-purple-900 ${BADGE_RING} ring-purple-600/25`;
        default:
            return `bg-neutral-50 text-neutral-800 ${BADGE_RING} ring-neutral-300`;
    }
}

export function creditReservationStatusBadgeClass(status: string): string {
    switch (status) {
        case 'committed':
            return `bg-emerald-50 text-emerald-900 ${BADGE_RING} ring-emerald-600/25`;
        case 'pending':
            return `bg-amber-50 text-amber-950 ${BADGE_RING} ring-amber-600/25`;
        case 'cancelled':
            return `bg-rose-50 text-rose-900 ${BADGE_RING} ring-rose-600/25`;
        default:
            return `bg-neutral-50 text-neutral-800 ${BADGE_RING} ring-neutral-300`;
    }
}

/** Pill styles for credit pool badges (admin table). */
export function ledgerPoolBadgeClass(poolType: string): string {
    switch (poolType) {
        case 'pro_credit':
            return `bg-blue-50 text-blue-900 ${BADGE_RING} ring-blue-600/25`;
        case 'pulse_credit':
            return `bg-violet-50 text-violet-900 ${BADGE_RING} ring-violet-600/25`;
        case 'professional_services_credit':
            return `bg-amber-50 text-amber-950 ${BADGE_RING} ring-amber-600/25`;
        case 'userlytics_credit':
            return `bg-cyan-50 text-cyan-950 ${BADGE_RING} ring-cyan-600/25`;
        case 'token':
            return `bg-slate-100 text-slate-800 ${BADGE_RING} ring-slate-400/35`;
        default:
            return `bg-neutral-50 text-neutral-800 ${BADGE_RING} ring-neutral-300`;
    }
}

function isNonChainableLedgerEntryType(entryType: string): boolean {
    return (
        entryType === 'adjustment' ||
        entryType === 'refund' ||
        entryType === 'reassignment' ||
        entryType === 'reassignment_out'
    );
}

function isActionableCommittedUsageRow(entry: LedgerEntrySummary): boolean {
    const entryType = getLedgerItemType(entry);
    return (
        (entryType === 'usage_commit' || entryType === 'reassignment_in') &&
        Number(entry.amount) > 0
    );
}

function isPoolScopedChainFollowUp(entry: LedgerEntrySummary): boolean {
    const entryType = getLedgerItemType(entry);
    return entryType === 'adjustment' || entryType === 'refund' || entryType === 'reassignment_out';
}

export function getLedgerItemType(entry: LedgerEntrySummary): string {
    return entry.type ?? entry.entryType;
}

export type LedgerAmountDirection = 'credit' | 'debit' | 'neutral';

/**
 * Effect of a ledger entry on the available balance. Stored amounts are positive
 * for most types, so the entry type determines whether credits are added back
 * (`credit`) or consumed (`debit`). Signed types (adjustment/rollover/reassignment)
 * and refunds fall back to the sign of the stored amount.
 */
export function getLedgerAmountDirection(entry: LedgerEntrySummary): LedgerAmountDirection {
    const entryType = getLedgerItemType(entry);
    const amount = Number(entry.amount);
    switch (entryType) {
        case 'grant':
        case 'rollover_in':
        case 'hold_release':
        case 'import':
            return 'credit';
        case 'usage':
        case 'usage_hold':
        case 'hold_adjustment':
        case 'usage_commit':
        case 'reassignment_in':
        case 'expiration':
        case 'rollover_out':
            return 'debit';
        case 'refund':
        case 'reassignment_out':
            // Refunds and reassignment out reverse usage and return credits; stored negative.
            return 'credit';
        case 'adjustment':
        case 'rollover':
        case 'reassignment':
            if (!Number.isFinite(amount) || amount === 0) return 'neutral';
            // A positive delta increases usage (debit); a negative delta returns credits.
            return amount > 0 ? 'debit' : 'credit';
        default:
            return 'neutral';
    }
}

/** Signed effect of an entry on the available balance (positive = credits added). */
export function getLedgerBalanceEffect(entry: LedgerEntrySummary): number {
    const amount = Number(entry.amount);
    if (!Number.isFinite(amount)) return 0;
    const direction = getLedgerAmountDirection(entry);
    if (direction === 'credit') return Math.abs(amount);
    if (direction === 'debit') return -Math.abs(amount);
    return amount;
}

/** Formats a usage amount: reductions get a leading minus, spend stays unsigned. */
export function formatLedgerUsageAmount(value: number): string {
    if (!Number.isFinite(value) || value === 0) {
        return formatCredits(Number.isFinite(value) ? value : 0);
    }
    const magnitude = formatCredits(Math.abs(value));
    return value > 0 ? magnitude : `\u2212${magnitude}`;
}

/**
 * Usage-centric signed amount for the ledger table: positive means credits were
 * spent (usage), negative means usage was reduced (a grant, refund, release, or a
 * downward adjustment). This is the inverse of the balance effect.
 */
export function getLedgerUsageAmount(entry: LedgerEntrySummary): number {
    return -getLedgerBalanceEffect(entry);
}

/**
 * Row-level Adjust is not offered for grants, releases, rollovers, hold adjustments,
 * adjustments, refunds, or reassignments.
 */
export function canAdjustAdminLedgerRow(
    entry: LedgerEntrySummary,
    ledgerEntries?: LedgerEntrySummary[],
): boolean {
    const entryType = getLedgerItemType(entry);
    const typeAllowed =
        entryType !== 'grant' &&
        entryType !== 'hold_release' &&
        entryType !== 'rollover' &&
        entryType !== 'rollover_in' &&
        entryType !== 'rollover_out' &&
        !isHoldAdjustmentEntry(entry) &&
        !isNonChainableLedgerEntryType(entryType);
    if (!typeAllowed) {
        return false;
    }
    if (ledgerEntries === undefined || !isActionableCommittedUsageRow(entry)) {
        return true;
    }
    return getRefundableLedgerAmount(entry, ledgerEntries) > 0;
}

/** Refunds reverse committed usage spend in the row's pool. */
export function canRefundAdminLedgerRow(
    entry: LedgerEntrySummary,
    ledgerEntries?: LedgerEntrySummary[],
): boolean {
    if (!isActionableCommittedUsageRow(entry)) {
        return false;
    }
    if (ledgerEntries === undefined) {
        return true;
    }
    return getRefundableLedgerAmount(entry, ledgerEntries) > 0;
}

/** Reassign moves net committed usage in the current pool to another SOW or pool. */
export function canReassignAdminLedgerRow(
    entry: LedgerEntrySummary,
    ledgerEntries?: LedgerEntrySummary[],
): boolean {
    return canRefundAdminLedgerRow(entry, ledgerEntries);
}

/** Cancel is offered on the original pending hold row for an open credit reservation. */
export function canCancelPendingCreditReservation(entry: LedgerEntrySummary): boolean {
    return (
        entry.state === 'pending' &&
        Boolean(entry.creditReservationId?.trim()) &&
        getLedgerItemType(entry) === 'usage_hold' &&
        !isHoldAdjustmentEntry(entry)
    );
}

function ledgerEntriesNewestFirst(entries: LedgerEntrySummary[]): LedgerEntrySummary[] {
    return [...entries].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
}

/**
 * Picks which row in a grouped batch should drive header actions. The newest row is often a
 * hold adjustment or follow-up entry that cannot be adjusted/cancelled/refunded.
 */
export function pickGroupedRowActionEntry(entries: LedgerEntrySummary[]): LedgerEntrySummary {
    const ordered = ledgerEntriesNewestFirst(entries);
    return (
        ordered.find((entry) => canCancelPendingCreditReservation(entry)) ??
        ordered.find((entry) => canRefundAdminLedgerRow(entry, entries)) ??
        ordered.find((entry) => canReassignAdminLedgerRow(entry, entries)) ??
        ordered.find((entry) => canAdjustAdminLedgerRow(entry, entries)) ??
        ordered[0]!
    );
}

/**
 * Net refundable amount for a usage row, including any follow-up entries already linked to it.
 * This keeps "full refund" aligned with the current effective usage total shown in the ledger thread.
 */
export function getRefundableLedgerAmount(
    sourceEntry: LedgerEntrySummary,
    ledgerEntries: LedgerEntrySummary[],
): number {
    const directAmount = Number(sourceEntry.amount);
    if (ledgerEntries.length === 0) {
        return directAmount;
    }

    let foundLinkedEntries = false;
    let total = 0;
    for (const entry of ledgerEntries) {
        if (entry.id !== sourceEntry.id && entry.linkedLedgerEntryId !== sourceEntry.id) {
            continue;
        }
        if (entry.id !== sourceEntry.id && !isPoolScopedChainFollowUp(entry)) {
            continue;
        }
        const amount = Number(entry.amount);
        if (!Number.isFinite(amount)) {
            continue;
        }
        foundLinkedEntries = true;
        total += amount;
    }

    return foundLinkedEntries ? total : directAmount;
}

function poolDisplayUsageAmounts(
    pool: SowBalanceSummary['pools'][number],
    usageScope: SowPoolUsageScope,
): { usageCommitted: number; usagePending: number } {
    if (usageScope === 'current_period') {
        const currentPeriod = (pool.periodSummaries ?? []).find((period) => period.isCurrent);
        if (currentPeriod) {
            return {
                usageCommitted: toNumber(currentPeriod.usageCommitted),
                usagePending: toNumber(currentPeriod.usagePending),
            };
        }
    }
    return {
        usageCommitted: toNumber(pool.usageCommitted),
        usagePending: toNumber(pool.usagePending),
    };
}

export function toPoolBalanceRows(balance: SowBalanceSummary | undefined): PoolBalanceRow[] {
    return (balance?.pools ?? []).map((pool) => {
        const contracted = toNumber(pool.contracted);
        const released = toNumber(pool.released ?? pool.granted);
        const fromRollover = toNumber(pool.fromRollover);
        const granted = toNumber(pool.granted || pool.released);
        const available = toNumber(pool.available);
        const expiredByPolicy = toNumber(pool.expiredByPolicy);
        const usageDisplay = resolveSowPoolUsageDisplayMetrics({
            granted: pool.granted,
            available: pool.available,
            usagePending: pool.usagePending,
            usageCommitted: pool.usageCommitted,
            resetPolicy: pool.resetPolicy,
            periodSummaries: pool.periodSummaries,
        });

        return {
            poolType: pool.poolType,
            label: getPoolLabel(pool.poolType),
            contracted,
            released,
            fromRollover,
            granted,
            usageGranted: usageDisplay.granted,
            usageScope: usageDisplay.usageScope,
            available,
            usageAvailable:
                usageDisplay.usageScope === 'current_period'
                    ? Math.max(usageDisplay.granted - usageDisplay.used, 0)
                    : available,
            used: usageDisplay.used,
            expiredByPolicy,
            usagePercent: usageDisplay.usagePercent,
            committedUsagePercent: usageDisplay.committedUsagePercent,
            pendingUsagePercent: usageDisplay.pendingUsagePercent,
            ...poolDisplayUsageAmounts(pool, usageDisplay.usageScope),
            resetPolicy: pool.resetPolicy,
            currentPeriodStart: isPeriodicReleasePool(pool.resetPolicy)
                ? pool.currentPeriodStart
                : null,
            currentPeriodEnd: isPeriodicReleasePool(pool.resetPolicy)
                ? pool.currentPeriodEnd
                : null,
            periodSummaries: pool.periodSummaries ?? [],
        };
    });
}
